import type { MetricsCollector, MetricsReport } from './metrics';

const PANEL_ID = 'metrics-panel';
const TOGGLE_KEY = '?';
const STORAGE_KEY = 'paperclip:metrics-panel:open';
const PERSISTENT_OPEN_DEFAULT = false;
const UPDATE_INTERVAL_MS = 500;

/**
 * Mount a small dev-only metrics panel that shows fps p50 / p95 and
 * the latest load-timing values. The panel is hidden by default and
 * toggled with the `?` key (also exposes itself as `window.__PAPERCLIP_DUMP_METRICS__()`
 * so QA can grab a JSON snapshot for the PR).
 */
export function mountMetricsPanel(collector: MetricsCollector): void {
  if (document.getElementById(PANEL_ID)) return;

  const panel = document.createElement('div');
  panel.id = PANEL_ID;
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', 'Performance metrics');
  panel.setAttribute('aria-hidden', 'true');
  panel.dataset.state = 'closed';

  const header = document.createElement('div');
  header.className = 'metrics-panel__header';
  header.textContent = 'metrics (press ? to toggle)';

  const body = document.createElement('pre');
  body.className = 'metrics-panel__body';
  body.tabIndex = 0;

  panel.appendChild(header);
  panel.appendChild(body);
  document.body.appendChild(panel);

  const setOpen = (open: boolean): void => {
    panel.dataset.state = open ? 'open' : 'closed';
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    try {
      localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
    } catch {
      // localStorage can be blocked (private mode, quota); ignore
    }
    if (open) {
      render(collector, body);
    }
  };

  const initialOpen = readInitialOpen();
  setOpen(initialOpen);

  window.addEventListener('keydown', (event) => {
    // Ignore when the user is typing in a form field
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    if (event.key === TOGGLE_KEY) {
      setOpen(panel.dataset.state !== 'open');
    }
  });

  // Re-render on report changes
  const unsubscribe = collector.subscribe((report) => {
    if (panel.dataset.state === 'open') {
      render(collector, body, report);
    }
  });

  // Periodic render in case no metric changed but a tick boundary hit
  const interval = window.setInterval(() => {
    if (panel.dataset.state === 'open') render(collector, body);
  }, UPDATE_INTERVAL_MS);

  // Expose a JSON dump helper so QA / scripts can capture the metrics
  // for the PR without poking at internals.
  window.__PAPERCLIP_DUMP_METRICS__ = (): MetricsReport => collector.getReport();

  // Clean up if the panel is removed (test isolation)
  const observer = new MutationObserver(() => {
    if (!panel.isConnected) {
      window.clearInterval(interval);
      unsubscribe();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true });
}

declare global {
  interface Window {
    __PAPERCLIP_DUMP_METRICS__?: () => MetricsReport;
  }
}

function readInitialOpen(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // ignore
  }
  return PERSISTENT_OPEN_DEFAULT;
}

function render(
  collector: MetricsCollector,
  body: HTMLElement,
  report: MetricsReport = collector.getReport(),
): void {
  const fps = report.fps;
  const load = report.load;
  const lines = [
    `fps p50: ${fps.fpsP50.toFixed(1)}  (p50 ${fps.p50Ms.toFixed(2)}ms)`,
    `fps p95: ${fps.fpsP95.toFixed(1)}  (p95 ${fps.p95Ms.toFixed(2)}ms)`,
    `fps max: ${fps.maxMs.toFixed(2)}ms   samples: ${fps.sampleCount}/${fps.windowSeconds}s`,
    '',
    `FCP:        ${fmt(load.fcpMs)}`,
    `LCP:        ${fmt(load.lcpMs)}`,
    `INP:        ${fmt(load.inpMs)}`,
    `CLS:        ${fmt(load.clsScore, 3)}`,
    `firstFrame: ${fmt(load.firstFrameMs)}`,
    `DCL:        ${fmt(load.domContentLoadedMs)}`,
    `load:       ${fmt(load.loadMs)}`,
    `bytes:      ${fmtBytes(load.transferSizeBytes)}`,
    '',
    `errors: ${report.errorCount}`,
  ];
  body.textContent = lines.join('\n');
}

function fmt(value: number | null, digits = 1): string {
  if (value === null || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}ms`;
}

function fmtBytes(value: number | null): string {
  if (value === null) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(2)} MB`;
}
